import { TestTarget, TestTargetFormData } from "../types/TestTarget";
import { SSTFApiClient, GraphQLTestTarget, TestTargetType } from "./api";
import { isFeatureTarget } from "./procedureClient";
import { format_error_message } from "../utils/errorMessage";

// TestTargetType管理システム
class TestTargetTypeManager {
  private static instance: TestTargetTypeManager | null = null;
  private testTargetTypes: TestTargetType[] = [];
  private nameToTypeIdMap: Map<string, string> = new Map();
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5分間キャッシュ

  static getInstance(): TestTargetTypeManager {
    if (!TestTargetTypeManager.instance) {
      TestTargetTypeManager.instance = new TestTargetTypeManager();
    }
    return TestTargetTypeManager.instance;
  }

  // TestTargetTypesを取得（キャッシュ機能付き）
  async getTestTargetTypes(
    apiClient: SSTFApiClient,
    workspaceId: string,
    projectId: string,
    forceRefresh: boolean = false
  ): Promise<TestTargetType[]> {
    const now = Date.now();

    if (
      !forceRefresh &&
      this.testTargetTypes.length > 0 &&
      now - this.lastFetchTime < this.CACHE_DURATION
    ) {
      console.log("TestTargetTypesキャッシュを使用");
      return this.testTargetTypes;
    }

    console.log("TestTargetTypesを新規取得中...");
    try {
      this.testTargetTypes = await apiClient.getTestTargetTypes(
        workspaceId,
        projectId
      );
      this.lastFetchTime = now;
      this.buildNameToTypeIdMap();

      console.log("TestTargetTypes取得成功:", this.testTargetTypes);
      return this.testTargetTypes;
    } catch (error) {
      console.error("TestTargetTypes取得エラー:", error);
      throw error;
    }
  }

  // name -> typeId マッピングを構築
  private buildNameToTypeIdMap(): void {
    this.nameToTypeIdMap.clear();

    this.testTargetTypes.forEach((type) => {
      // name をそのまま使用
      this.nameToTypeIdMap.set(type.name, type.typeId);

      // 小文字版も追加
      this.nameToTypeIdMap.set(type.name.toLowerCase(), type.typeId);

      // よくある別名も追加
      const aliases = this.getTypeAliases(type.name.toLowerCase());
      aliases.forEach((alias) => {
        this.nameToTypeIdMap.set(alias, type.typeId);
      });
    });

    console.log(
      "name->typeIdマッピング構築完了:",
      Array.from(this.nameToTypeIdMap.entries())
    );
  }

  // 型名の別名を取得
  private getTypeAliases(typeName: string): string[] {
    const aliases: string[] = [];

    switch (typeName.toLowerCase()) {
      case "service":
        aliases.push("services");
        break;
      case "view":
        aliases.push("views", "screen", "screens", "page", "pages");
        break;
      case "component":
        aliases.push("components", "common_component", "common_components");
        break;
      case "item":
        aliases.push("items", "element", "elements");
        break;
      case "section":
        aliases.push("sections");
        break;
      case "overlay":
        aliases.push("overlays", "modal", "dialog", "popup", "tooltip");
        break;
      case "category":
        aliases.push("categories", "folder", "folders");
        break;
      case "componentref":
        // buildNameToTypeIdMap は SSTF 名 "ComponentRef" を小文字化("componentref")して渡すため、
        // ローカル union 値（ハイフン付き "component-ref"）と snake_case を別名登録する。
        // これにより種別→typeId 解決（作成/保存パスの getTypeIdByName）で component-ref を引ける。
        aliases.push("component-ref", "component_ref");
        break;
    }

    return aliases;
  }

  // 型名からtypeIdを取得
  getTypeIdByName(typeName: string): string | null {
    if (!typeName) return null;

    const typeId =
      this.nameToTypeIdMap.get(typeName) ||
      this.nameToTypeIdMap.get(typeName.toLowerCase());
    if (typeId) {
      console.log(`型名 '${typeName}' -> typeId: ${typeId}`);
      return typeId;
    }

    console.warn(`型名 '${typeName}' に対応するtypeIdが見つかりません`);
    return null;
  }

  // 利用可能な型名一覧を取得
  getAvailableTypeNames(): string[] {
    return this.testTargetTypes.map((type) => type.name);
  }

  // キャッシュをクリア
  clearCache(): void {
    this.testTargetTypes = [];
    this.nameToTypeIdMap.clear();
    this.lastFetchTime = 0;
    console.log("TestTargetTypesキャッシュをクリアしました");
  }
}

// TestTargetTypeの定義（新仕様に対応）
// 大文字小文字を区別せず、複数のバリエーションに対応
const TEST_TARGET_TYPE_MAP: Record<
  string,
  | "service"
  | "view"
  | "component"
  | "section"
  | "overlay"
  | "item"
  | "category"
  | "component-ref"
> = {
  // 小文字版
  service: "service",
  services: "service",
  view: "view",
  views: "view",
  screen: "view",
  screens: "view",
  page: "view",
  pages: "view",
  component: "component",
  components: "component",
  section: "section",
  sections: "section",
  overlay: "overlay",
  overlays: "overlay",
  modal: "overlay",
  dialog: "overlay",
  popup: "overlay",
  tooltip: "overlay",
  item: "item",
  items: "item",
  element: "item",
  elements: "item",
  common_component: "component",
  common_components: "component",
  // category（フォルダー的コンテナ。SSTF の Category 型）
  category: "category",
  categories: "category",
  // component-ref（SSTF の ComponentRef 型）— 小文字・ハイフン・アンダースコア・各バリエーション
  "component-ref": "component-ref",
  component_ref: "component-ref",
  componentref: "component-ref", // ComponentRef.toLowerCase() のハイフン無し版
  // 大文字版（GraphQLレスポンス用）
  Service: "service",
  Services: "service",
  View: "view",
  Views: "view",
  Screen: "view",
  Screens: "view",
  Page: "view",
  Pages: "view",
  Component: "component",
  Components: "component",
  Section: "section",
  Sections: "section",
  Overlay: "overlay",
  Overlays: "overlay",
  Modal: "overlay",
  Dialog: "overlay",
  Popup: "overlay",
  Tooltip: "overlay",
  Item: "item",
  Items: "item",
  Element: "item",
  Elements: "item",
  Category: "category",
  Categories: "category",
  // component-ref 大文字版
  ComponentRef: "component-ref",
  ComponentRefs: "component-ref",
  componentRef: "component-ref",
};

// 型名を正規化してマッピングする関数
export const getTestTargetType = (
  typeName: string
):
  | "service"
  | "view"
  | "component"
  | "section"
  | "overlay"
  | "item"
  | "category"
  | "component-ref" => {
  if (!typeName) {
    console.warn("型名が空です。デフォルトの'item'を使用します。");
    return "item";
  }

  // まず元の型名でそのまま試行（大文字小文字を維持）
  const trimmedTypeName = typeName.trim();
  let mappedType = TEST_TARGET_TYPE_MAP[trimmedTypeName];

  if (mappedType) {
    return mappedType;
  }

  // 直接マッチしない場合は小文字にして再試行
  const normalizedTypeName = trimmedTypeName.toLowerCase();
  mappedType = TEST_TARGET_TYPE_MAP[normalizedTypeName];

  if (mappedType) {
    return mappedType;
  }

  // マッピングが見つからない場合はデフォルトを使用
  console.warn(
    `未知の型名 '${typeName}' です。デフォルトの'item'を使用します。`
  );
  return "item";
};

// スネークケースをキャメルケースに変換
const snakeToCamel = (obj: Record<string, any>): Record<string, any> => {
  const converted: Record<string, any> = {};

  // スキップすべきキャメルケースのキー（すでに存在する場合は変換を優先）
  const camelCaseKeysToSkip = [
    "forceAnalyze",
    "isDownload",
    "isManual",
    "failCount",
  ];

  for (const [key, value] of Object.entries(obj)) {
    // すでにキャメルケースのキーはスキップ（スネークケースからの変換を優先）
    if (camelCaseKeysToSkip.includes(key)) {
      continue;
    }

    // 特定のキーのみ変換
    if (key === "force_analyze") {
      converted["forceAnalyze"] = value;
    } else if (key === "is_download") {
      converted["isDownload"] = value;
    } else if (key === "is_manual") {
      converted["isManual"] = value;
    } else if (key === "fail_count") {
      converted["failCount"] = value;
    } else {
      converted[key] = value;
    }
  }

  return converted;
};

// キャメルケースをスネークケースに変換
const camelToSnake = (obj: any): Record<string, any> => {
  const converted: Record<string, any> = {};

  // スキップすべきスネークケースのキー（すでに変換済み or 重複を避けるため）
  const snakeCaseKeysToSkip = [
    "force_analyze",
    "is_download",
    "is_manual",
    "fail_count",
  ];

  for (const [key, value] of Object.entries(obj)) {
    // すでにスネークケースのキーはスキップ（重複を避ける）
    if (snakeCaseKeysToSkip.includes(key)) {
      continue;
    }

    // 特定のキーのみ変換
    if (key === "forceAnalyze") {
      converted["force_analyze"] = value;
    } else if (key === "isDownload") {
      converted["is_download"] = value;
    } else if (key === "isManual") {
      converted["is_manual"] = value;
    } else if (key === "failCount") {
      converted["fail_count"] = value;
    } else {
      converted[key] = value;
    }
  }

  return converted;
};

// GraphQLから取得したTestTargetをTestTarget構造に変換
const convertGraphQLTestTargetsToTestTargets = (
  graphqlTestTargets: GraphQLTestTarget[]
): TestTarget[] => {
  const convertedTargets: TestTarget[] = [];

  graphqlTestTargets.forEach((target) => {
    try {
      if (!target.type) {
        console.warn(`TestTarget '${target.name}' にtype情報がありません。`);
      }

      const testTargetType = getTestTargetType(target.type?.name);

      // paramsをパース（JSON文字列の場合）
      let parsedParams: Record<string, unknown> = {};
      if (target.params) {
        try {
          parsedParams = JSON.parse(target.params);
          // スネークケースをキャメルケースに変換
          parsedParams = snakeToCamel(parsedParams);

          // デバッグ: params内のstatusを確認
          if (testTargetType === "item" && (parsedParams as any).itemDetails) {
            console.log(`📋 [${target.name}] itemDetails:`, {
              hasUsecases: !!(parsedParams as any).itemDetails.usecases,
              usecasesCount: (parsedParams as any).itemDetails.usecases?.length,
              firstUsecase: (parsedParams as any).itemDetails.usecases?.[0],
              hasPolicies: !!(parsedParams as any).itemDetails.policies,
              policiesCount: (parsedParams as any).itemDetails.policies?.length,
              firstPolicy: (parsedParams as any).itemDetails.policies?.[0],
              hasTransitions: !!(parsedParams as any).transitions,
              transitionsCount: (parsedParams as any).transitions?.length,
              firstTransition: (parsedParams as any).transitions?.[0],
            });
          }
        } catch (parseError) {
          console.warn("params解析エラー:", parseError);
          parsedParams = {};
        }
      }

      const meta = target.metaInfo;
      const convertedTarget: TestTarget = {
        id: target.testTargetId,
        name: target.name,
        type: testTargetType,
        description: target.description || "",
        displayOrder: target.displayOrder,
        parentId: target.parentTestTargetId || undefined,
        isFeature: isFeatureTarget(target),
        params: parsedParams,
        metaInfo: {
          createdAt: meta?.createdAt ?? "",
          createdBy: meta?.createdBy ?? "",
          modifiedAt: meta?.modifiedAt ?? "",
          modifiedBy: meta?.modifiedBy ?? "",
        },
      };

      // タイプに応じてparamsを設定
      switch (testTargetType) {
        case "service":
        case "view":
        case "component":
        case "section":
        case "overlay":
        case "category":
          convertedTarget.params = parsedParams as any;
          break;

        case "item":
          // transitionsの後方互換性処理
          if ((parsedParams as any).transitions) {
            const transitions = (parsedParams as any).transitions;
            // 単一オブジェクトの場合は配列に変換
            if (!Array.isArray(transitions)) {
              (parsedParams as any).transitions = [transitions];
            }
          }
          convertedTarget.params = parsedParams as any;
          break;

        case "component-ref":
          // TODO(#811): component-ref の params（referenceTargetId 等）の UI 表示・編集は後続スライスで実装。
          // ここでは生 params をそのまま保持し、既存動作を壊さない。
          convertedTarget.params = parsedParams as any;
          break;
      }

      convertedTargets.push(convertedTarget);
    } catch (error) {
      console.error(`TestTarget '${target.name}' の変換中にエラー:`, error);
    }
  });

  return convertedTargets;
};

// API対応のfetchTestTargets関数
export const fetchTestTargets = async (
  apiClient?: SSTFApiClient | null,
  workspaceId?: string,
  projectId?: string
): Promise<TestTarget[]> => {
  if (apiClient && workspaceId && projectId) {
    try {
      const graphqlTestTargets = await apiClient.getTestTargets(
        workspaceId,
        projectId
      );
      if (graphqlTestTargets.length === 0) {
        return [];
      }
      return convertGraphQLTestTargetsToTestTargets(graphqlTestTargets);
    } catch (error) {
      console.error("GraphQL取得エラー:", error);
      throw error;
    }
  }
  throw new Error("API設定が不完全です。");
};

// TestTargetType管理システムのインスタンスを取得するヘルパー関数
export const getTestTargetTypeManager = (): TestTargetTypeManager => {
  return TestTargetTypeManager.getInstance();
};

// 型名からtypeIdを取得するヘルパー関数
export const getTypeIdFromTypeName = async (
  typeName: string,
  apiClient: SSTFApiClient,
  workspaceId: string,
  projectId: string
): Promise<string | null> => {
  const typeManager = TestTargetTypeManager.getInstance();

  // まずキャッシュから試す
  let typeId = typeManager.getTypeIdByName(typeName);
  if (typeId) {
    return typeId;
  }

  // キャッシュにない場合は最新データを取得
  try {
    await typeManager.getTestTargetTypes(
      apiClient,
      workspaceId,
      projectId,
      true
    );
    return typeManager.getTypeIdByName(typeName);
  } catch (error) {
    console.error("TestTargetTypes取得中にエラー:", error);
    return null;
  }
};

export const createTestTarget = async (
  data: TestTargetFormData,
  apiClient?: SSTFApiClient | null,
  workspaceId?: string,
  projectId?: string
): Promise<TestTarget> => {
  if (!apiClient || !workspaceId || !projectId) {
    throw new Error("API設定が不完全です。");
  }

  console.log("TestTarget作成開始:", data);

  // 型名からtypeIdを取得
  const typeId = await getTypeIdFromTypeName(
    data.type,
    apiClient,
    workspaceId,
    projectId
  );
  if (!typeId) {
    throw new Error(`未知の型名です: ${data.type}`);
  }

  // paramsをキャメルケースからスネークケースに変換してJSON文字列に変換
  const convertedParams = data.params ? camelToSnake(data.params) : {};
  const params = JSON.stringify(convertedParams);

  const createInput = {
    name: data.name.trim(),
    displayOrder: data.displayOrder || 1.0,
    typeId,
    description: data.description || undefined,
    parentTestTargetId: data.parentId || undefined,
    params,
  };

  console.log("TestTarget作成リクエスト:", createInput);

  try {
    const createdTargets = await apiClient.createTestTargets(
      workspaceId,
      projectId,
      [createInput]
    );
    const createdTarget = createdTargets[0];

    console.log("TestTarget作成成功:", createdTarget);

    // GraphQLTestTarget を TestTarget に変換
    return convertGraphQLTestTargetsToTestTargets([createdTarget])[0];
  } catch (error) {
    console.error("TestTarget作成エラー:", error);
    const errorMessage = format_error_message(error);
    throw new Error(`TestTargetの作成に失敗しました: ${errorMessage}`);
  }
};

export const updateTestTarget = async (
  id: string,
  data: TestTargetFormData,
  apiClient?: SSTFApiClient | null,
  workspaceId?: string,
  projectId?: string
): Promise<TestTarget> => {
  if (!apiClient || !workspaceId || !projectId) {
    throw new Error("API設定が不完全です。");
  }

  console.log("TestTarget更新開始:", { id, data });

  try {
    // APIクライアントの既存メソッドを使用
    const updatedTarget = await apiClient.updateTestTarget(
      workspaceId,
      projectId,
      id,
      data
    );

    console.log("TestTarget更新成功:", updatedTarget);

    // GraphQLTestTarget を TestTarget に変換
    return convertGraphQLTestTargetsToTestTargets([updatedTarget])[0];
  } catch (error) {
    console.error("TestTarget更新エラー:", error);
    throw new Error(
      `TestTargetの更新に失敗しました: ${
        error instanceof Error ? error.message : "不明なエラー"
      }`
    );
  }
};

export interface BulkCreateInput {
  tempKey: string;
  data: TestTargetFormData;
}

export const createTestTargetsBulk = async (
  inputs: BulkCreateInput[],
  apiClient?: SSTFApiClient | null,
  workspaceId?: string,
  projectId?: string
): Promise<{ idMap: Map<string, string> }> => {
  if (!apiClient || !workspaceId || !projectId) {
    throw new Error("API設定が不完全です。");
  }
  if (inputs.length === 0) return { idMap: new Map() };

  const typeIdCache = new Map<string, string>();
  const resolveTypeId = async (typeName: string): Promise<string> => {
    const cached = typeIdCache.get(typeName);
    if (cached) return cached;
    const typeId = await getTypeIdFromTypeName(
      typeName,
      apiClient,
      workspaceId,
      projectId
    );
    if (!typeId) throw new Error(`未知の型名です: ${typeName}`);
    typeIdCache.set(typeName, typeId);
    return typeId;
  };

  const createInputs = [];
  for (const { data } of inputs) {
    const typeId = await resolveTypeId(data.type);
    createInputs.push({
      name: data.name.trim(),
      displayOrder: data.displayOrder || 1.0,
      typeId,
      description: data.description || undefined,
      parentTestTargetId: data.parentId || undefined,
      // SSTFApiClient.createTestTargets 側で params の camel -> snake 変換を行う。
      // ここで先に変換すると force_analyze 等が二重変換時に skip されて欠落する。
      params: JSON.stringify(data.params ?? {}),
    });
  }

  const created = await apiClient.createTestTargets(
    workspaceId,
    projectId,
    createInputs
  );

  // (name, parentTestTargetId) で実 ID を引く。返却順は保証しない前提のため name+親で紐付ける。
  // 同一バッチ内の同名・同親は SSTF の duplicateNameAtSameLevel 制約（仕様書インポートでも
  // DUPLICATE_NAME_AT_SAME_LEVEL で事前検証）により存在せず、このキーは一意。
  const lookup = new Map<string, string>();
  for (const c of created) {
    lookup.set(
      `${c.name} ${c.parentTestTargetId ?? ""}`,
      c.testTargetId
    );
  }

  const idMap = new Map<string, string>();
  for (const { tempKey, data } of inputs) {
    const realId = lookup.get(`${data.name.trim()} ${data.parentId ?? ""}`);
    if (realId) idMap.set(tempKey, realId);
  }
  return { idMap };
};

export interface BulkUpdateInput {
  id: string;
  data: TestTargetFormData;
}

export const updateTestTargetsBulk = async (
  inputs: BulkUpdateInput[],
  apiClient?: SSTFApiClient | null,
  workspaceId?: string,
  projectId?: string,
): Promise<void> => {
  if (!apiClient || !workspaceId || !projectId) {
    throw new Error("API設定が不完全です。");
  }
  if (inputs.length === 0) return;

  const typeIdCache = new Map<string, string>();
  const resolveTypeId = async (typeName: string): Promise<string> => {
    const cached = typeIdCache.get(typeName);
    if (cached) return cached;
    const typeId = await getTypeIdFromTypeName(typeName, apiClient, workspaceId, projectId);
    if (!typeId) throw new Error(`未知の型名です: ${typeName}`);
    typeIdCache.set(typeName, typeId);
    return typeId;
  };

  const updateInputs = [];
  for (const { id, data } of inputs) {
    const typeId = await resolveTypeId(data.type);
    const parentTestTargetId = data.type === "service" ? null : (data.parentId ?? undefined);
    updateInputs.push({
      testTargetId: id,
      name: data.name.trim(),
      displayOrder: data.displayOrder || 1.0,
      typeId,
      description: data.description || undefined,
      parentTestTargetId,
      params: JSON.stringify(data.params ? camelToSnake(data.params) : {}),
    });
  }

  await apiClient.updateTestTargets(workspaceId, projectId, updateInputs);
};

export const deleteTestTargetsBulk = async (
  ids: string[],
  apiClient?: SSTFApiClient | null,
  workspaceId?: string,
  projectId?: string,
): Promise<void> => {
  if (!apiClient || !workspaceId || !projectId) {
    throw new Error("API設定が不完全です。");
  }
  if (ids.length === 0) return;
  await apiClient.deleteTestTargets(workspaceId, projectId, ids);
};

export const deleteTestTarget = async (
  id: string,
  apiClient?: SSTFApiClient | null,
  workspaceId?: string,
  projectId?: string
): Promise<void> => {
  if (!apiClient || !workspaceId || !projectId) {
    throw new Error("API設定が不完全です。");
  }

  console.log("TestTarget削除開始:", { id });

  try {
    // APIクライアントの既存メソッドを使用
    await apiClient.deleteTestTarget(workspaceId, projectId, id);

    console.log("TestTarget削除成功:", id);
  } catch (error) {
    console.error("TestTarget削除エラー:", error);
    throw new Error(
      `TestTargetの削除に失敗しました: ${
        error instanceof Error ? error.message : "不明なエラー"
      }`
    );
  }
};
